import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, Grid, IconButton, Modal, Stack, TextField, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { REQUIRED_RED } from '../../../App';
import { MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';
import { handlePreventKeyDownDecimal } from '../../../utility/helpers/commonFunction';

const MODAL_STYLE = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#FFF',
    borderRadius: '12px',
    boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
    overflow: 'hidden',
    width: '500px',
    maxWidth: 'calc(100vw - 24px)',
};

const LABEL_STYLE = {
    color: 'rgba(0,0,0,0.87)',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    mb: '8px',
};

const ProgramModal = ({ open, onClose, onSave, updateIndex, setUpdateIndex, discountProgram = [], handleSubmitProgram }) => {
    const [nameError, setNameError] = useState('');

    const isEditMode = updateIndex !== -1;

    const {
        handleSubmit,
        setValue,
        control,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            programName: '',
            discount: '',
        },
    });

    /**
     * Populate values in edit mode
     */
    useEffect(() => {
        if (isEditMode && discountProgram?.[updateIndex]) {
            const currentProgram = discountProgram[updateIndex];

            setValue('programName', currentProgram?.programName || '');
            setValue('discount', currentProgram?.discount || '');
        } else {
            reset();
        }
    }, [discountProgram, isEditMode, reset, setValue, updateIndex]);

    /**
     * Close modal
     */
    const handleClose = useCallback(() => {
        setUpdateIndex(-1);
        reset();
        setNameError('');
        onClose(false);
    }, [onClose, reset, setUpdateIndex]);

    /**
     * Submit form
     */
    const handleFormSubmit = useCallback(
        (values) => {
            setNameError('');

            const existingProgram = discountProgram.find(
                (program, index) => program.programName.trim().toLowerCase() === values.programName.trim().toLowerCase() && index !== updateIndex
            );

            if (existingProgram) {
                setNameError('Program name already exists. Please choose a different name.');
                return;
            }

            const payload = {
                programName: values.programName.trim(),
                discount: Number(values.discount).toFixed(2),
            };

            let updatedPrograms = [];

            if (isEditMode) {
                updatedPrograms = [...discountProgram];

                updatedPrograms[updateIndex] = {
                    ...updatedPrograms[updateIndex],
                    ...payload,
                };
            } else {
                updatedPrograms = [...discountProgram, payload];
            }

            onSave(updatedPrograms);
            handleSubmitProgram?.(undefined, updatedPrograms);

            handleClose();
        },
        [discountProgram, handleClose, handleSubmitProgram, isEditMode, onSave, updateIndex]
    );

    return (
        <Modal
            open={open}
            onClose={(event, reason) => {
                if (reason && reason === 'backdropClick') {
                    return;
                }
                handleClose();
            }}
        >
            <Box sx={MODAL_STYLE}>
                {/* HEADER */}
                <Box
                    sx={{
                        height: '64px',
                        px: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(0,0,0,0.10) !important',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '20px',
                            fontWeight: 500,
                            color: '#1F1F1F',
                            lineHeight: '32px',
                        }}
                    >
                        {isEditMode ? 'Edit Program' : 'Add New Program'}
                    </Typography>

                    <IconButton
                        onClick={handleClose}
                        sx={{
                            width: 36,
                            height: 36,
                            mr: '-8px',
                            color: '#1F1F1F',
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 30 }} />
                    </IconButton>
                </Box>

                {/* FORM */}
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Box
                        sx={{
                            px: '24px',
                            pt: '24px',
                            pb: '22px',
                        }}
                    >
                        <Grid container spacing={3}>
                            {/* PROGRAM NAME */}
                            <Grid item xs={12} sm={8}>
                                <Typography sx={LABEL_STYLE}>
                                    Program Name
                                    <span
                                        style={{
                                            color: REQUIRED_RED,
                                            marginLeft: 4,
                                        }}
                                    >
                                        *
                                    </span>
                                </Typography>

                                <Controller
                                    name="programName"
                                    control={control}
                                    rules={{
                                        required: 'Please enter program name',
                                        minLength: {
                                            value: 2,
                                            message: 'Program name must be at least 2 characters',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            variant="outlined"
                                            error={!!errors.programName || !!nameError}
                                            helperText={errors.programName ? errors.programName.message : nameError}
                                            sx={MuiTextFieldWithFocus}
                                            inputProps={{
                                                maxLength: 50,
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* DISCOUNT */}
                            <Grid item xs={12} sm={4}>
                                <Typography sx={LABEL_STYLE}>
                                    Discount %
                                    <span
                                        style={{
                                            color: REQUIRED_RED,
                                            marginLeft: 4,
                                        }}
                                    >
                                        *
                                    </span>
                                </Typography>

                                <Controller
                                    name="discount"
                                    control={control}
                                    rules={{
                                        required: 'Please enter discount percentage',
                                        min: {
                                            value: 0,
                                            message: 'Discount cannot be less than 0%',
                                        },
                                        max: {
                                            value: 100,
                                            message: 'Discount cannot be more than 100%',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            type="number"
                                            variant="outlined"
                                            error={!!errors.discount}
                                            helperText={errors.discount?.message || ''}
                                            onKeyDown={handlePreventKeyDownDecimal}
                                            sx={MuiTextFieldWithFocus}
                                            inputProps={{
                                                min: 0,
                                                max: 100,
                                                step: 0.01,
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider
                        sx={{
                            borderColor: 'rgba(0,0,0,0.12)',
                        }}
                    />

                    {/* FOOTER */}
                    <Box
                        sx={{
                            px: '16px',
                            py: '16px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleClose}
                                sx={{
                                    minWidth: 100,
                                    height: 42,
                                }}
                            >
                                CANCEL
                            </Button>

                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                sx={{
                                    minWidth: 82,
                                    height: 42,
                                }}
                            >
                                {isEditMode ? 'UPDATE' : 'SAVE'}
                            </Button>
                        </Stack>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default memo(ProgramModal);
